import {
  CloseOutlined,
  DownCircleOutlined,
  ReloadOutlined,
  UpCircleOutlined,
} from "@ant-design/icons";
import { Col, Button, Tooltip, notification } from "antd";
import moment from "moment";
import React, {
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect,
} from "react";
import Carousel from "react-elastic-carousel";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import SearchModal from "../components/Search";
import { commonConstants } from "../constants";
import { projectListingHelper } from "../helpers";
import { convertValue, GlobalUnitEnum } from "../helpers/unit-conversion";
import {
  TOGGLE_PROJECT_DETAILS,
  TOGGLE_PROJECT_LIST,
} from "../redux/application/types";
import { SET_SELECTED_PROJECT } from "../redux/project/types";

import "../global-styles/antd-custom.scss";

const parseWeldingSystems = (welding_system) => {
  console.log(welding_system, "welding_system");
  try {
    const weldingSystemObj = JSON.parse(welding_system || "{}");
    const weldingSystems = Object.keys(weldingSystemObj).filter(
      (key) => weldingSystemObj[key]
    );
    console.log(weldingSystems, "weldingSystems");
    return weldingSystems.length > 0 ? weldingSystems.join(", ") : "-";
  } catch (error) {
    return welding_system || "-";
  }
};

const formatProjectDate = (dateValue) =>
  dateValue ? moment(new Date(dateValue)).format("DD-MMM-YYYY") : "-";

const getWeldAssignmentType = (weldAssignmentType) => {
  if (!weldAssignmentType) return "Manual";
  return weldAssignmentType === "BAR" ? "Manual" : "Automatic";
};

const getCarouselConfig = (width) => {
  if (width < 100) return { itemsToShow: 1, itemsToScroll: 1 };
  if (width < 200) return { itemsToShow: 2, itemsToScroll: 1 };
  if (width < 500) return { itemsToShow: 3, itemsToScroll: 1 };
  if (width < 800) return { itemsToShow: 4, itemsToScroll: 1 };
  if (width < 1280) return { itemsToShow: 6, itemsToScroll: 1 };
  if (width < 1440) return { itemsToShow: 6, itemsToScroll: 1 };
  if (width < 1600) return { itemsToShow: 7, itemsToScroll: 1 };
  if (width < 1920) return { itemsToShow: 8, itemsToScroll: 1 };
  if (width < 2400) return { itemsToShow: 11, itemsToScroll: 1 };
  return { itemsToShow: 12, itemsToScroll: 1 };
};

const ProjectDetailsCarousel = ({ selectedProject }) => {
  const { t } = useTranslation();

  // Get the global unit setting from the selected project
  const globalUnit = selectedProject?.globalUnit || GlobalUnitEnum.IMPERIAL;

  const renderProjectDetail = (project) => {
    const { value } = project;
    const projectValue = selectedProject[value];

    switch (value) {
      case "welding_system":
        return `${parseWeldingSystems(projectValue)} ${
          selectedProject["iwm"] != "false" ? ",IWM" : ""
        }`;
      case "welding_assignment_type":
        return getWeldAssignmentType(projectValue);
      case "start_date":
      case "end_date":
        return formatProjectDate(projectValue);
      case "welds_completed":
        return `${projectValue || 0} of ${selectedProject.welds_planned || 0}`;
      case "iwm":
        return projectValue ? t("common.yes") : t("common.no");
      case "num_stations":
        return Math.floor(projectValue || 0);
      case "pipe_thickness":
        // Apply unit conversion for wall thickness
        if (projectValue && !isNaN(parseFloat(projectValue))) {
          const convertedValue = convertValue(
            projectValue,
            globalUnit === GlobalUnitEnum.METRIC ? "cm" : "in"
          );
          return convertedValue;
        }
        return projectValue || "-";
      case "pipe_diameter":
        // Apply unit conversion for pipe diameter
        if (projectValue && !isNaN(parseFloat(projectValue))) {
          const convertedValue = convertValue(
            projectValue,
            globalUnit === GlobalUnitEnum.METRIC ? "cm" : "in"
          );
          return convertedValue;
        }
        return projectValue || "-";
      default:
        return projectValue || "-";
    }
  };

  // Get the appropriate label based on global unit setting
  const getLabel = (project) => {
    const { name } = project;

    // Update labels for pipe diameter and wall thickness based on global unit
    if (name === "common.wallthickness") {
      return globalUnit === GlobalUnitEnum.METRIC
        ? "Wall Thickness (cm)"
        : t(name);
    }
    if (name === "common.pipediameter") {
      return globalUnit === GlobalUnitEnum.METRIC
        ? "Pipe Diameter (cm)"
        : t(name);
    }

    return t(name);
  };

  return (
    <div className="project-detail-bar">
      <div className="project-detail-bar-items h-100 mb-3">
        <div className="flex-fill overflow-auto">
          <div className="flexslider-bt flexslider-small h-100">
            <Carousel
              pagination={false}
              breakPoints={commonConstants.carouselConfig}
            >
              {projectListingHelper.displayFields?.map((project) => (
                <Col key={project.value} span={24}>
                  <div className="project-details-wrapper text-center">
                    <p className="fw-medium title">{getLabel(project)}</p>
                    <div className="d-flex edit-session justify-content-center mb-2">
                      <p className="mb-0">{renderProjectDetail(project)}</p>
                    </div>
                  </div>
                </Col>
              ))}
            </Carousel>
          </div>
        </div>
      </div>
    </div>
  );
};
ProjectDetailsCarousel.displayName = "ProjectDetailsCarousel";

const ProjectListCarousel = ({
  projectListData,
  selectedProject,
  onProjectSelect,
  showProjects,
  carouselRef,
}) => {
  const carouselConfig = useMemo(
    () =>
      [100, 200, 500, 800, 1280, 1440, 1600, 1920, 2400].map((width) => ({
        width,
        ...getCarouselConfig(width),
      })),
    []
  );

  const getCarouselClassName = (projectListLength) =>
    projectListLength <= 5 ? "fourflexslider h-100" : "flexslider h-100";

  return (
    <div
      className={
        showProjects ? "project-list-carousel" : "project-list-carousel-hidden"
      }
    >
      {projectListData?.length > 0 && (
        <div className="flex-fill overflow-auto">
          <div className={getCarouselClassName(projectListData.length)}>
            <Carousel
              ref={carouselRef}
              pagination={false}
              breakPoints={carouselConfig}
            >
              {projectListData
                .slice()
                .reverse()
                .map((data, index) => (
                  <div
                    className="float-start slides-divs slides"
                    key={`${data.project_id}-${index}`}
                  >
                    <a
                      id={data.project_id}
                      onClick={() =>
                        onProjectSelect(data.project_id, data.project_name)
                      }
                      className={
                        data.project_id === selectedProject?.project_id &&
                        data.project_name === selectedProject?.project_name
                          ? "slides-items active"
                          : "slides-items"
                      }
                    >
                      <ProjectListItem
                        data={data}
                        selectedProject={selectedProject}
                      />
                    </a>
                  </div>
                ))}
            </Carousel>
          </div>
        </div>
      )}
    </div>
  );
};
ProjectListCarousel.displayName = "ProjectListCarousel";

const ProjectListItem = React.memo(({ data, selectedProject }) => {
  const truncateText = (text, maxLength = 20) =>
    text.length > maxLength ? `${text.substring(0, 19)}...` : text;

  const isSelected =
    data.project_id === selectedProject?.project_id &&
    data.project_name === selectedProject?.project_name;

  return (
    <>
      <div className="text-center tooltip-parent">
        <span className={isSelected ? "tooltip-text-active" : "tooltip-text"}>
          {data.project_name.length > 20
            ? data.project_name
                .substring(0, 30)
                .concat(`\n ${data.project_name.substring(30)}`)
            : ""}
        </span>
        <span>{truncateText(data.project_name)}</span>
      </div>
      <div className="text-center tooltip-parent">
        <span>{truncateText(data.project_number)}</span>
        <span
          className={
            isSelected ? "tooltip-text-active-bottom" : "tooltip-text-bottom"
          }
        >
          {data.project_number.length > 20
            ? data.project_number
                .substring(0, 30)
                .concat(`\n ${data.project_number.substring(30)}`)
            : ""}
        </span>
      </div>
    </>
  );
});
ProjectListItem.displayName = "ProjectListItem";

// Main ProjectListHeader Component
const ProjectListHeader = ({
  reloadData,
  projectListData,
  setProjectList,
  handlerClearSearch,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const reducerList = useSelector((state) => state);
  const carouselRef = useRef(null);

  const [searchText, setSearchText] = useState("");
  const [showProjects, setShowProjects] = useState(true);
  const [showCarousel, setShowCarousel] = useState(true);

  const selectedProject = reducerList?.projectReducer?.selectedProject;

  const handleProjectClick = useCallback(() => {
    const newShowProjects = !showProjects;
    setShowProjects(newShowProjects);
    dispatch({
      type: TOGGLE_PROJECT_LIST,
      payload: newShowProjects,
    });
  }, [showProjects, dispatch]);

  const handleTitleClick = useCallback(() => {
    const newShowCarousel = !showCarousel;
    setShowCarousel(newShowCarousel);
    dispatch({
      type: TOGGLE_PROJECT_DETAILS,
      payload: newShowCarousel,
    });
  }, [showCarousel, dispatch]);

  const handleSearch = useCallback((searchTerm) => {
    setSearchText(searchTerm.toLowerCase());
  }, []);

  const handleClickSearch = useCallback(
    ({ callBack }) => {
      if (!searchText) {
        notification.warn({
          message: t("projectDetails.enterProjectName"),
        });
        return;
      }

      const filteredProjects = reducerList.projectReducer.projectList?.filter(
        (project) =>
          project.project_name.toLowerCase().includes(searchText) ||
          project.project_number.toLowerCase().includes(searchText)
      );

      if (filteredProjects.length > 0) {
        setProjectList(filteredProjects);
        dispatch({
          type: SET_SELECTED_PROJECT,
          payload: {},
        });

        if (carouselRef.current) {
          carouselRef.current.goTo(0);
        }

        callBack();
      } else {
        notification.error({
          message: `${t("projectDetails.noProject")} ${searchText}`,
        });
      }
    },
    [
      searchText,
      t,
      reducerList.projectReducer.projectList,
      dispatch,
      setProjectList,
    ]
  );

  const getSelectedProject = useCallback(
    (project_id, project_name) => {
      const project = projectListData?.find(
        (item) =>
          item.project_id === project_id && item.project_name === project_name
      );

      if (project) {
        dispatch({
          type: SET_SELECTED_PROJECT,
          payload: project,
        });

        // Simple carousel navigation - go to the selected project
        if (carouselRef.current) {
          const reversedList = projectListData.slice().reverse();
          const selectedIndex = reversedList.findIndex(
            (p) =>
              p.project_id === project_id && p.project_name === project_name
          );

          if (selectedIndex !== -1) {
            carouselRef.current.goTo(selectedIndex);
          }
        }
      }
    },
    [projectListData, dispatch]
  );

  useEffect(() => {
    const selectedProject = reducerList?.projectReducer?.selectedProject;
    if (selectedProject) {
      getSelectedProject(
        selectedProject.project_id,
        selectedProject.project_name
      );
    }
  }, [
    projectListData,
    reducerList?.projectReducer?.selectedProject?.project_id,
    reducerList?.projectReducer?.selectedProject?.project_name,
    dispatch,
  ]);

  return (
    <>
      <div className="project-carousel">
        <div className="project-list" onClick={handleProjectClick}>
          {!showProjects ? (
            <DownCircleOutlined className="downoutlined-icon-projectList" />
          ) : (
            <UpCircleOutlined className="upoutlined" />
          )}{" "}
          <h1 className="project-title">{t("common.projects")}</h1>
          <h6 className="mx-3 mt-1">
            {!showProjects && Object.keys(selectedProject || {}).length ? (
              <b>{`${selectedProject.project_number}\t-\t${selectedProject.project_name}`}</b>
            ) : (
              ""
            )}
          </h6>
        </div>
        <div className="search-container d-flex">
          {searchText && (
            <div className="top-bar-input d-flex justify-content-between align-items-center me-3">
              <span>{searchText}</span>{" "}
              <a
                className="export-link"
                onClick={() => {
                  setSearchText("");
                  handlerClearSearch();
                }}
              >
                <CloseOutlined />
              </a>{" "}
            </div>
          )}
          <SearchModal
            onSearch={(e) => handleSearch(e.target.value)}
            searchText={searchText}
            setValue={setSearchText}
            handleClickSearch={handleClickSearch}
            projectNumbers={projectListData}
          />
          <Tooltip placement="topLeft" title="Reload">
            <Button
              className="refresh ms-3"
              shape="circle"
              onClick={reloadData}
            >
              <ReloadOutlined />
            </Button>
          </Tooltip>
        </div>
      </div>

      <div>
        <ProjectListCarousel
          projectListData={projectListData}
          selectedProject={selectedProject}
          onProjectSelect={getSelectedProject}
          showProjects={showProjects}
          carouselRef={carouselRef}
        />
        <div className="custom-line mb-3" />
      </div>

      {selectedProject?.project_id && !showCarousel && (
        <div className="project-details-hide" onClick={handleTitleClick}>
          <DownCircleOutlined className="downoutlined-icon" />
          <h6 className="p-details-title">
            {t("projectDetails.projectDetail")}
          </h6>
          <div className="custom-line mb-3 p-d" />
        </div>
      )}

      {selectedProject?.project_id && showCarousel && (
        <div>
          <div className="p-d-f">
            <UpCircleOutlined
              onClick={handleTitleClick}
              className="upoutlined"
            />
          </div>
          <ProjectDetailsCarousel selectedProject={selectedProject} />
        </div>
      )}
    </>
  );
};

export default React.memo(ProjectListHeader);
